import { useCallback, useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useGridState } from '@hooks/use-grid-state';
import {
  useCreateTender,
  useTenderStatusCounts,
  useTenders,
} from '@/api/sourcing/sourcing.hooks';
import type { Tender, TenderStatus } from '@/api/sourcing/sourcing.types';
import {
  DsButton,
  DsInput,
  DsModal,
  DsSearchInput,
  DsTabNav,
  DsTextarea,
} from '../ds';
import type { DsTab } from '../ds';
import { ContainedPage } from '../shared/contained-page.component';
import { DsTable } from '../shared/ds-table.component';
import { STATUS_LABEL, fmtDate, statusPillClass } from './helpers';
import { fromDateTimeLocal } from './helpers';
import './sourcing.css';

type StatusFilter = 'all' | TenderStatus;

const col = createColumnHelper<Tender>();

const columns = [
  col.accessor('reference', {
    header: 'Nr.',
    size: 120,
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('title', {
    header: 'Titel',
    meta: { flex: true },
    cell: (info) => (
      <span style={{ fontWeight: 500, color: 'var(--ink)' }}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('status', {
    header: 'Status',
    size: 120,
    cell: (info) => (
      <span className={`ds-pill ${statusPillClass(info.getValue())}`}>
        {STATUS_LABEL[info.getValue()]}
      </span>
    ),
  }),
  col.accessor('deadlineAt', {
    header: 'Frist',
    size: 100,
    cell: (info) => (
      <span
        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}
      >
        {fmtDate(info.getValue())}
      </span>
    ),
  }),
  col.accessor('createdAt', {
    header: 'Erstellt',
    size: 100,
    cell: (info) => (
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--ink-3)',
        }}
      >
        {fmtDate(info.getValue())}
      </span>
    ),
  }),
];

export function SourcingPage() {
  const { getCustom, setCustom } = useGridState('sourcing-v2');
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const statusFilter = getCustom<StatusFilter>('status', 'all');
  const setStatusFilter = useCallback(
    (v: string) => setCustom('status', v),
    [setCustom],
  );
  const query = getCustom<string>('query', '');
  const setQuery = useCallback((v: string) => setCustom('query', v), [setCustom]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isPending } = useTenders({
    status: statusFilter === 'all' ? undefined : statusFilter,
    q: query || undefined,
    limit: 500,
  });

  const { data: counts } = useTenderStatusCounts();

  const statusTabs: DsTab[] = useMemo(() => {
    const total = counts
      ? counts.DRAFT +
        counts.PUBLISHED +
        counts.CLOSED +
        counts.AWARDED +
        counts.CANCELLED
      : undefined;
    return [
      { id: 'all', label: 'Alle', count: total },
      { id: 'DRAFT', label: STATUS_LABEL.DRAFT, count: counts?.DRAFT },
      {
        id: 'PUBLISHED',
        label: STATUS_LABEL.PUBLISHED,
        count: counts?.PUBLISHED,
      },
      { id: 'CLOSED', label: STATUS_LABEL.CLOSED, count: counts?.CLOSED },
      { id: 'AWARDED', label: STATUS_LABEL.AWARDED, count: counts?.AWARDED },
      {
        id: 'CANCELLED',
        label: STATUS_LABEL.CANCELLED,
        count: counts?.CANCELLED,
      },
    ];
  }, [counts]);

  const rows = useMemo(() => data?.data ?? [], [data]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableSortingRemoval: false,
  });

  return (
    <div className="sc-main">
      <DsTabNav
        tabs={statusTabs}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        maxWidth={1200}
      />

      <ContainedPage
        toolbar={
          <>
            <DsSearchInput
              className="sc-search"
              value={query}
              onSearch={setQuery}
              debounce={250}
              placeholder="Nr. oder Titel…"
            />
            <span className="sc-spacer" />
            <DsButton
              variant="primary"
              size="small"
              onClick={() => setCreateOpen(true)}
            >
              + Neue Ausschreibung
            </DsButton>
          </>
        }
        contained
      >
        {isPending ? (
          <div className="sc-empty">Lädt…</div>
        ) : (
          <DsTable
            table={table}
            fixedLayout
            onRowClick={(t) => navigate(`/i/sourcing/${t.id}`)}
            emptyMessage="Keine Ausschreibungen gefunden."
          />
        )}
      </ContainedPage>

      {createOpen && (
        <CreateTenderModal
          onClose={() => setCreateOpen(false)}
          onCreated={(tender) => {
            setCreateOpen(false);
            enqueueSnackbar(`${tender.reference} erstellt`, {
              variant: 'success',
            });
            navigate(`/i/sourcing/${tender.id}`);
          }}
        />
      )}
    </div>
  );
}

function CreateTenderModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (tender: Tender) => void;
}) {
  const createTender = useCreateTender();
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');

  const handleCreate = () => {
    if (!title.trim()) {
      enqueueSnackbar('Titel ist erforderlich.', { variant: 'error' });
      return;
    }
    createTender.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        deadlineAt: fromDateTimeLocal(deadline) ?? undefined,
      },
      {
        onSuccess: onCreated,
        onError: () =>
          enqueueSnackbar('Erstellen fehlgeschlagen.', { variant: 'error' }),
      },
    );
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title="Neue Ausschreibung"
      description="Eine neue Ausschreibung wird als Entwurf angelegt."
      footer={
        <>
          <DsButton onClick={onClose}>Abbrechen</DsButton>
          <DsButton
            variant="primary"
            disabled={createTender.isPending}
            onClick={handleCreate}
          >
            {createTender.isPending ? 'Erstellt…' : 'Erstellen'}
          </DsButton>
        </>
      }
    >
      <div className="sc-form">
        <label className="sc-field">
          <span className="sc-field-label">Titel</span>
          <DsInput
            value={title}
            autoFocus
            placeholder="z. B. Notebooks Q3"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="sc-field">
          <span className="sc-field-label">Beschreibung (für Lieferanten)</span>
          <DsTextarea
            value={description}
            rows={4}
            placeholder="Kontext zur Ausschreibung…"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="sc-field">
          <span className="sc-field-label">Angebotsfrist</span>
          <input
            type="datetime-local"
            className="ds-input"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </label>
      </div>
    </DsModal>
  );
}
